import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of, throwError, from } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

export interface ScannerAgentConfig {
  /** Where CoreAxisScanAgentSetup.exe is hosted. Empty means "not published yet". */
  downloadUrl: string;
  /** Agents older than this are prompted to update. */
  minVersion: string;
}

/**
 * Supplied by the app, which owns the environment files — a library cannot import from an
 * app under the Nx boundary rules. The default keeps the library usable standalone and makes
 * an unconfigured build degrade to install instructions rather than a broken link.
 */
export const SCANNER_AGENT_CONFIG = new InjectionToken<ScannerAgentConfig>(
  'SCANNER_AGENT_CONFIG',
  {
    providedIn: 'root',
    factory: (): ScannerAgentConfig => ({ downloadUrl: '', minVersion: '1.0.0' }),
  },
);

/**
 * Talks to the CoreAxis Scan Agent running on the user's own machine.
 *
 * Scanning cannot happen on the server: WIA and TWAIN are OS-level driver protocols, and a
 * server has no path to a scanner plugged into someone else's desk. The agent is a small tray
 * application the user installs, exposing the scanner over loopback HTTP.
 *
 * The response shapes here deliberately match what the old server endpoint returned, so the
 * scan component's preview, rotate, reorder and upload pipeline needed no changes.
 */

export interface AgentScannerDevice {
  deviceId: string;
  name: string;
  source?: string;
}

export interface AgentScanOptions {
  deviceId: string;
  dpi?: number;
  colorMode?: 'color' | 'grayscale' | 'blackwhite';
  format?: 'jpeg' | 'png';
}

export interface AgentScanResult {
  fileName: string;
  mimeType: string;
  base64Data: string;
  fileSizeBytes: number;
}

interface AgentHealth {
  name: string;
  version: string;
  port: number;
}

/** Identifies our agent rather than whatever else might hold the port. */
const AGENT_NAME = 'CoreAxisScanAgent';

/** Must match the agent's own fallback list. */
const AGENT_PORTS = [47285, 47286, 47287, 47288, 47289];

/** A local process answers in single-digit milliseconds; anything slower is not our agent. */
const PROBE_TIMEOUT_MS = 1500;

/** Scanners are mechanical — first page out of standby can genuinely take this long. */
const SCAN_TIMEOUT_MS = 120_000;

@Injectable({ providedIn: 'root' })
export class ScannerAgentService {
  /**
   * Built on HttpBackend so the app's HTTP interceptors are skipped.
   *
   * The auth interceptor stamps Authorization: Bearer <jwt> on every HttpClient request. Sent to
   * 127.0.0.1 that does two kinds of damage: the extra header forces a CORS preflight the agent
   * rejects (it only allows Content-Type), so the agent looked absent even while running; and it
   * hands the user's session token to a local process that has no use for it. The agent is
   * gated by origin, not by the user's identity, so these calls go out bare.
   */
  private readonly http = new HttpClient(inject(HttpBackend));
  readonly config = inject(SCANNER_AGENT_CONFIG);

  /** Remembered between calls so every scan does not re-walk the port range. */
  private baseUrl: string | null = null;

  /** False when no installer has been published yet — the UI then explains instead of linking. */
  get hasDownloadUrl(): boolean {
    return !!this.config.downloadUrl?.trim();
  }

  get downloadUrl(): string {
    return this.config.downloadUrl?.trim() ?? '';
  }

  /**
   * Starts the installer download.
   *
   * Chrome and Edge discard executable downloads that are not tied to a user gesture, so this
   * is reliable from a click and best-effort otherwise. Callers must keep the download button
   * visible regardless of what this returns — never show a spinner waiting on it.
   */
  triggerDownload(): boolean {
    if (!this.hasDownloadUrl) return false;

    try {
      const link = document.createElement('a');
      link.href = this.downloadUrl;
      link.download = '';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Resolves the agent's base URL, probing the port range on first use.
   *
   * 127.0.0.1 rather than "localhost": the latter can resolve to ::1 first, where the agent is
   * not listening, which shows up as an intermittent failure on some machines.
   */
  private resolveBaseUrl(): Observable<string> {
    if (this.baseUrl) return of(this.baseUrl);

    return from(this.probePorts()).pipe(
      switchMap((url) =>
        url
          ? of(url)
          : throwError(() => new Error('AGENT_NOT_FOUND')),
      ),
    );
  }

  private async probePorts(): Promise<string | null> {
    for (const port of AGENT_PORTS) {
      const candidate = `http://127.0.0.1:${port}`;

      try {
        const health = await this.fetchHealth(candidate);

        // A port being open is not enough — it must actually be our agent.
        if (health?.name === AGENT_NAME) {
          this.baseUrl = candidate;
          return candidate;
        }
      } catch {
        // Nothing listening, or not ours. Try the next port.
      }
    }

    return null;
  }

  private fetchHealth(baseUrl: string): Promise<AgentHealth | null> {
    return new Promise((resolve) => {
      this.http
        .get<AgentHealth>(`${baseUrl}/health`)
        .pipe(
          timeout(PROBE_TIMEOUT_MS),
          catchError(() => of(null)),
        )
        .subscribe((result) => resolve(result));
    });
  }

  /** Clears the cached URL so the next call re-probes — used after a prompted install. */
  reset(): void {
    this.baseUrl = null;
  }

  /** Resolves to the running agent's version, or null when no agent is reachable. */
  getVersion(): Observable<string | null> {
    this.reset();

    return from(this.probePorts()).pipe(
      switchMap((url) => (url ? from(this.fetchHealth(url)) : of(null))),
      map((health) => health?.version ?? null),
      catchError(() => of(null)),
    );
  }

  listScanners(): Observable<AgentScannerDevice[]> {
    return this.resolveBaseUrl().pipe(
      switchMap((baseUrl) =>
        this.http.get<AgentScannerDevice[]>(`${baseUrl}/scanners`).pipe(timeout(PROBE_TIMEOUT_MS * 4)),
      ),
      catchError((err) => {
        // A dropped connection mid-session usually means the user quit the agent; forget the
        // cached URL so the next attempt re-probes rather than failing against a dead port.
        this.reset();
        return throwError(() => err);
      }),
    );
  }

  scan(options: AgentScanOptions): Observable<AgentScanResult> {
    return this.resolveBaseUrl().pipe(
      switchMap((baseUrl) =>
        this.http.post<AgentScanResult>(`${baseUrl}/scan`, options).pipe(timeout(SCAN_TIMEOUT_MS)),
      ),
    );
  }

  /** True when the failure was "no agent installed" rather than a scanner or network problem. */
  static isAgentMissing(err: unknown): boolean {
    return err instanceof Error && err.message === 'AGENT_NOT_FOUND';
  }

  /**
   * Compares dotted version strings. Missing segments count as zero, so "1.2" < "1.2.1".
   * Returns true when `version` is older than `minimum`.
   */
  static isOlderThan(version: string | null, minimum: string): boolean {
    if (!version) return false;

    const actual = version.split('.').map((n) => parseInt(n, 10) || 0);
    const required = minimum.split('.').map((n) => parseInt(n, 10) || 0);
    const length = Math.max(actual.length, required.length);

    for (let i = 0; i < length; i++) {
      const a = actual[i] ?? 0;
      const r = required[i] ?? 0;
      if (a !== r) return a < r;
    }

    return false;
  }
}
